<template>
  <header class="mobile-header">
    <!-- Logo (Centered) -->
    <div class="mobile-logo-bar">
      <img :src="logoPath" alt="BioEdge" class="mobile-logo-center" />
      <button class="header-action-btn" @click="toggleCart" :aria-label="`Cart (${cartCount})`">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.15.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
        </svg>
        <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
      </button>
    </div>

    <!-- Search Bar -->
    <div class="mobile-search-bar">
      <div class="search-input-wrapper">
        <svg class="search-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search products..."
          class="search-input"
          @input="handleSearchInput"
        />
        <button
          v-if="searchQuery"
          class="search-clear-btn"
          @click="clearSearch"
          aria-label="Clear search"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Quick Search Results -->
    <div v-if="showSearchResults && searchResults.length > 0" class="search-results">
      <div
        v-for="(product, index) in searchResults"
        :key="product.firebaseId || product.id || index"
        class="search-result-item"
        @click="selectProduct(product)"
      >
        <img
          :src="searchResultImageUrl(product)"
          :alt="product.name"
          class="search-result-image"
        />
        <div class="search-result-info">
          <h4 class="search-result-name">{{ product.name }}</h4>
          <p class="search-result-price">R{{ formatPrice(product.price) }}</p>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import logoImage from '../assets/dark.png'
import { getProductImageUrl } from '../utils/productImageUrl'

export default {
  name: 'MobileHeader',
  props: {
    cartCount: {
      type: Number,
      default: 0
    },
    products: {
      type: Array,
      default: () => []
    }
  },
  emits: ['search', 'filter', 'cart', 'product-select'],
  data() {
    return {
      searchQuery: '',
      showSearchResults: false,
      logoPath: logoImage
    }
  },
  computed: {
    searchResults() {
      if (!this.searchQuery.trim() || !this.products.length) return []
      const query = this.searchQuery.toLowerCase().trim()
      return this.products
        .filter(product => {
          const name = (product.name || '').toLowerCase()
          const description = (product.description || '').toLowerCase()
          const category = (product.category || '').toLowerCase()
          return name.includes(query) || description.includes(query) || category.includes(query)
        })
        .slice(0, 5) // Show top 5 results
    }
  },
  watch: {
    searchQuery(newVal) {
      this.showSearchResults = newVal.trim().length > 0
      this.$emit('search', newVal)
    }
  },
  methods: {
    searchResultImageUrl(product) {
      return (
        getProductImageUrl(product) ||
        'https://via.placeholder.com/50/f3f4f6/52CD36?text=·'
      )
    },
    toggleCart() {
      // Emit cart event to parent, which will handle showing the drawer
      this.$emit('cart')
    },
    handleSearchInput(event) {
      this.searchQuery = event.target.value
    },
    clearSearch() {
      this.searchQuery = ''
      this.showSearchResults = false
    },
    selectProduct(product) {
      const productId = product.firebaseId || product.id
      const productName = product.name
      
      // Navigate to product view page
      if (productId) {
        this.$router.push({ path: `/product/${productId}`, query: { name: productName } })
      } else if (productName) {
        this.$router.push({ path: '/product', query: { name: productName } })
      }
      
      this.searchQuery = ''
      this.showSearchResults = false
    },
    formatPrice(price) {
      if (typeof price === 'number') {
        return price.toLocaleString('en-ZA')
      }
      if (typeof price === 'string') {
        const numericPrice = parseFloat(price.replace(/[^\d.]/g, '')) || 0
        return numericPrice.toLocaleString('en-ZA')
      }
      return '0'
    }
  }
}
</script>

<style scoped>
.mobile-header {
  background: linear-gradient(135deg, #52CD36 0%, #3da026 100%);
  color: white;
  padding: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mobile-logo-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1rem;
  position: relative;
}

.mobile-logo-center {
  height: 4.5rem;
  width: auto;
  object-fit: contain;
  max-width: 200px;
}

.header-action-btn {
  position: absolute;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.header-action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.header-action-btn svg {
  width: 1.25rem;
  height: 1.25rem;
  fill: white;
}

.cart-badge {
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  background: #ff4444;
  color: white;
  border-radius: 50%;
  width: 1.125rem;
  height: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
}

.mobile-search-bar {
  padding: 0.75rem 1rem 1rem;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
}

.search-icon {
  width: 1.25rem;
  height: 1.25rem;
  fill: #6b7280;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.875rem;
  font-family: 'Montserrat', sans-serif;
  color: #111827;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-action-btn {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-clear-btn {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-clear-btn svg {
  width: 1.25rem;
  height: 1.25rem;
  fill: #6b7280;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e5e7eb;
  max-height: 400px;
  overflow-y: auto;
  z-index: 200;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background 0.2s;
}

.search-result-item:hover {
  background: #f9fafb;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-image {
  width: 50px;
  height: 50px;
  border-radius: 0.5rem;
  object-fit: cover;
  background: #f3f4f6;
}

.search-result-info {
  flex: 1;
}

.search-result-name {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
}

.search-result-price {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: #52CD36;
}

@media (min-width: 1024px) {
  .mobile-header {
    display: none;
  }
}
</style>

